"use client";

// @ubm/ui/components/dialog — LE MOTEUR COMMUN des couches de dialogue (Dialog, Drawer, BottomSheet, façade
// `@ubm/product-ui` Dialog).
//
// CORRECTION U2 (05/10/2026, revue FIN1 point 2) — LE CONTENU NE MONTE PLUS `DialogPrimitive.Content`. Mesure Root
// (`inactive-scope-initial-20261005-result.json`, FocusScope Radix 1.1.9 réel) : un `FocusScope` monté plus tard, même
// d'une couche inactive, met en pause la boucle de la couche active ; et le `DismissableLayer` d'une couche inactive
// restée montée prend l'Échap de la pile Radix. Aucun réglage public de `DialogPrimitive.Content` ne suspend ces deux
// effets sans démonter le contenu (Radix bascule de branche entre modal et non modal), ce qui perdrait un brouillon
// React local. Adaptation causale minimale, DANS ce moteur partagé (pas un second moteur) :
//   · Radix garde ce qui ne porte aucun effet global : Root (état ouvert), Trigger, Portal (Presence), Title,
//     Description, Close ;
//   · le contenu est un nœud `role="dialog"` STABLE (même type, même place quelle que soit l'engagement : l'état React
//     des enfants survit), dont la mécanique vient du crochet partagé déjà suspendable `useOverlayAccessible` — boucle
//     de Tab (piège unique), Échap de la couche du dessus par la pile commune `data-ubm-overlay` (K10), inertie et
//     verrou si modal, focus d'entrée et retour à l'origine — et le clic extérieur logique de `useCoucheNonModale`.
//     Sous un hôte désengagé, TOUT est suspendu : aucune pile de bibliothèque n'y reste montée.
//   · Les rappels publics sont conservés avec leur sémantique (annulables par `preventDefault()`) : `onEscapeKeyDown`,
//     `onPointerDownOutside`, `onFocusOutside`, `onInteractOutside` (événement à `detail.originalEvent`),
//     `onOpenAutoFocus`, `onCloseAutoFocus` ; ids et liens ARIA (`aria-labelledby` vers le titre, `aria-describedby`
//     vers la description présente, `aria-controls` du déclencheur) ; `asChild`, `forceMount`, `modal` (défaut true).
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Slot } from "@radix-ui/react-slot";
import { cloneElement, createContext, forwardRef, isValidElement, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, CSSProperties, ElementRef, ReactElement, ReactNode, Ref, RefCallback, RefObject } from "react";
import { useReducedMotionConfig as useReducedMotion } from "motion/react";
import { useOverlayAccessible, useCoucheNonModale, ATTRIBUT_VOILE } from "../../overlays/useOverlayAccessible";
import { attributsEngagement, useOverlayHostEngaged } from "../../overlays/OverlayHost";

export interface DialogEngineState {
  open: boolean;
  modal: boolean;
}

interface EngineContext extends DialogEngineState {
  setOpen: (open: boolean) => void;
  contentId: string;
  titleId: string;
  descriptionId: string;
  described: boolean;
  setDescribed: (present: boolean) => void;
  triggerRef: RefObject<HTMLElement | null>;
}

const DialogEngineContext = createContext<EngineContext | null>(null);

/** L'état logique du Root courant (`null` hors Root du moteur). */
export function useDialogEngine(): DialogEngineState | null {
  const engine = useContext(DialogEngineContext);
  return engine ? { open: engine.open, modal: engine.modal } : null;
}

/** Fusionne des références (objet ou rappel). */
export function composeRefs<T>(...refs: (Ref<T> | undefined)[]): RefCallback<T> {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref != null) (ref as { current: T | null }).current = node;
    }
  };
}

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export interface RootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Modal (inertie, verrou, Tab bouclé, focus extérieur refusé). Défaut `true`, comme Radix. */
  modal?: boolean;
  children?: ReactNode;
}

/** Root contrôlé OU non contrôlé, compatible Radix. L'ouverture reste logique sous un hôte désengagé. */
export function Root({ open: openProp, defaultOpen = false, onOpenChange, modal = true, children }: RootProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = openProp ?? uncontrolled;
  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolled(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  const base = useId();
  const [described, setDescribed] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const value = useMemo<EngineContext>(
    () => ({ open, modal, setOpen, contentId: `${base}-content`, titleId: `${base}-title`, descriptionId: `${base}-description`, described, setDescribed, triggerRef }),
    [open, modal, setOpen, base, described],
  );
  // Radix ne porte ici que l'état partagé par Trigger / Portal / Title / Description / Close (aucun contenu Radix monté).
  return (
    <DialogEngineContext.Provider value={value}>
      <DialogPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
        {children}
      </DialogPrimitive.Root>
    </DialogEngineContext.Provider>
  );
}

export const Trigger = forwardRef<ElementRef<typeof DialogPrimitive.Trigger>, ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>>(function Trigger(props, ref) {
  const engine = useContext(DialogEngineContext);
  const composed = useMemo(() => composeRefs<HTMLButtonElement>(ref, engine ? (node) => { engine.triggerRef.current = node; } : undefined), [ref, engine]);
  return <DialogPrimitive.Trigger aria-controls={engine?.contentId} {...props} ref={composed} />;
});

export const Portal = DialogPrimitive.Portal;
export const Close = DialogPrimitive.Close;

export const Title = forwardRef<ElementRef<typeof DialogPrimitive.Title>, ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(function Title(props, ref) {
  const engine = useContext(DialogEngineContext);
  return <DialogPrimitive.Title id={engine?.titleId} {...props} ref={ref} />;
});

export const Description = forwardRef<ElementRef<typeof DialogPrimitive.Description>, ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(function Description(props, ref) {
  const engine = useContext(DialogEngineContext);
  const setDescribed = engine?.setDescribed;
  useIsoLayoutEffect(() => {
    if (!setDescribed) return;
    setDescribed(true);
    return () => setDescribed(false);
  }, [setDescribed]);
  return <DialogPrimitive.Description id={engine?.descriptionId} {...props} ref={ref} />;
});

export interface OverlayProps extends ComponentPropsWithoutRef<"div"> {
  asChild?: boolean;
  /** Rester monté fermé (sortie animée par l'appelant). */
  forceMount?: true;
}

/**
 * Le voile. Rendu tant que le Root est ouvert (ou `forceMount`). Il porte la marque de voile : l'inertie du crochet ne
 * l'atteint pas, il reste la cible du clic qui ferme. Désengagé, il porte `data-ubm-overlay-engaged="false"`.
 */
export const Overlay = forwardRef<HTMLDivElement, OverlayProps>(function Overlay({ asChild, forceMount, style, children, ...props }, ref) {
  const engine = useContext(DialogEngineContext);
  const engaged = useOverlayHostEngaged();
  const reduced = useReducedMotion();
  const open = engine?.open ?? true;
  if (!open && !forceMount) return null;
  const Comp = asChild ? Slot : "div";
  return <Comp data-state={open ? "open" : "closed"} data-motion={reduced ? "reduced" : undefined} {...{ [ATTRIBUT_VOILE]: "" }} {...attributsEngagement(engaged)} {...props} {...suspension(engaged, style)} ref={ref}>{enfantSuspendu(asChild, engaged, children)}</Comp>;
});

/**
 * SUSPENSION FONCTIONNELLE (supplément U2, 05/10/2026) — indépendante de toute classe CSS et de l'élément `asChild` : un voile
 * ou un contenu d'hôte désengagé devient `inert` (ni focus, ni clic, ni lecteur d'écran), `aria-hidden` et
 * `visibility: hidden`, en style FUSIONNÉ (le style du consommateur, valeurs Motion comprises, est conservé). Le nœud reste
 * monté, même type, même place : l'état React et les brouillons survivent ; réengagé, ces effets disparaissent. Hôte engagé :
 * rien n'est ajouté — une sortie animée normale (`!open` + `forceMount`) n'est jamais masquée.
 */
function suspension(engaged: boolean, style: CSSProperties | undefined): { inert?: boolean; "aria-hidden"?: true; style?: CSSProperties } {
  if (engaged) return style === undefined ? {} : { style };
  return { inert: true, "aria-hidden": true, style: { ...style, visibility: "hidden" } };
}

/**
 * CORRECTION SLOT (U2, 05/10/2026 — défaut reproduit par Root, React 19.2.7 / Radix Slot 1.3.0) : avec `asChild`, le Slot fait
 * GAGNER les props de l'enfant ; un enfant qui porte `inert={false}`, `aria-hidden={false}` ou `visibility: visible` annulait la
 * suspension posée sur le Slot. Sous hôte désengagé, la suspension est donc imposée sur l'ENFANT FINAL lui-même (`cloneElement` :
 * même type, même clé, même ref, mêmes handlers — aucune remontée, état et saisies conservés ; son style est fusionné, la
 * visibilité forcée en dernier). Hôte engagé : l'enfant est rendu TEL QUEL, ses props et styles d'origine reviennent.
 */
function enfantSuspendu(asChild: boolean | undefined, engaged: boolean, children: ReactNode): ReactNode {
  if (!asChild || engaged || !isValidElement(children)) return children;
  const style = (children.props as { style?: CSSProperties }).style;
  return cloneElement(children as ReactElement<Record<string, unknown>>, { inert: true, "aria-hidden": true, style: { ...style, visibility: "hidden" } });
}

/** Événement d'interaction extérieure (forme Radix : `detail.originalEvent`), annulable. */
export type PointerDownOutsideEvent = CustomEvent<{ originalEvent: Event }>;
export type FocusOutsideEvent = CustomEvent<{ originalEvent: FocusEvent }>;

export interface ContentProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  asChild?: boolean;
  /** Rester monté fermé (sortie animée par l'appelant). */
  forceMount?: true;
  /** Conservé pour compatibilité : le focus d'entrée est désormais toujours porté par le crochet partagé. */
  focusEntree?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: PointerDownOutsideEvent) => void;
  onFocusOutside?: (event: FocusOutsideEvent) => void;
  onInteractOutside?: (event: PointerDownOutsideEvent | FocusOutsideEvent) => void;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

const annulable = (type: string) => new Event(type, { cancelable: true });

/** Contenu bas niveau, SANS chrome (ni en-tête ni bouton fermer imposés). `asChild` pour un `motion.div`. */
export const Content = forwardRef<HTMLDivElement, ContentProps>(function Content(
  {
    asChild,
    forceMount,
    focusEntree: _focusEntree,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onPointerDownCapture,
    onFocusCapture,
    style,
    children,
    ...props
  },
  ref,
) {
  void _focusEntree; // retiré des attributs DOM ; sans effet (voir le type)
  const engine = useContext(DialogEngineContext);
  const engaged = useOverlayHostEngaged();
  const reduced = useReducedMotion();
  const open = engine?.open ?? true;
  const modal = engine?.modal ?? true;
  const fermer = engine?.setOpen;
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useMemo<RefObject<HTMLElement | null>>(() => engine?.triggerRef ?? { current: null }, [engine?.triggerRef]);

  // Mécanique modale commune (suspendue sous hôte désengagé) : boucle de Tab, Échap de la couche du dessus, inertie et
  // verrou si modal, focus d'entrée, retour à l'origine. Rappels utilisateur annulables avant chaque effet.
  const accessible = useOverlayAccessible<HTMLDivElement>({
    ouvert: open,
    onFermer: () => fermer?.(false),
    piegeFocus: true,
    echapFerme: true,
    verrouilleDefilement: modal,
    inertieArrierePlan: modal,
    avantEchap: (event) => {
      onEscapeKeyDown?.(event);
      return !event.defaultPrevented;
    },
    avantFocusEntree: () => {
      const event = annulable("dialog.openAutoFocus");
      onOpenAutoFocus?.(event);
      return !event.defaultPrevented;
    },
    avantRetourFocus: () => {
      const event = annulable("dialog.closeAutoFocus");
      onCloseAutoFocus?.(event);
      return !event.defaultPrevented;
    },
  });

  // Appui extérieur logique (identité de l'événement natif ; un descendant porté — liste d'un Select — reste intérieur ;
  // le déclencheur est exclu). Le voile est extérieur : un appui dessus ferme, comme la source.
  const couche = useCoucheNonModale({
    ouvert: open,
    racine: nodeRef,
    ancre: triggerRef,
    exterieur: "pointerdown",
    echap: false,
    onFermer: (_raison, originalEvent) => {
      const event: PointerDownOutsideEvent = new CustomEvent("dialog.pointerDownOutside", { cancelable: true, detail: { originalEvent } });
      onPointerDownOutside?.(event);
      onInteractOutside?.(event);
      if (!event.defaultPrevented) fermer?.(false);
    },
  });

  // Focus extérieur : un dialogue MODAL ne le laisse pas sortir (boucle + inertie) et ne se ferme jamais par là ; un
  // dialogue non modal se ferme comme Radix, sauf `preventDefault()`. Un focus dans un descendant porté reste intérieur.
  const focusInterieurRef = useRef<Event | null>(null);
  const rappelsFocus = useRef({ onFocusOutside, onInteractOutside });
  rappelsFocus.current = { onFocusOutside, onInteractOutside };
  useEffect(() => {
    if (!open || !engaged || modal) return;
    const surFocus = (e: FocusEvent) => {
      const logique = focusInterieurRef.current === e;
      focusInterieurRef.current = null;
      const cible = e.target as Node | null;
      if (logique || (cible !== null && nodeRef.current?.contains(cible))) return;
      const event: FocusOutsideEvent = new CustomEvent("dialog.focusOutside", { cancelable: true, detail: { originalEvent: e } });
      rappelsFocus.current.onFocusOutside?.(event);
      rappelsFocus.current.onInteractOutside?.(event);
      if (!event.defaultPrevented) fermer?.(false);
    };
    document.addEventListener("focusin", surFocus);
    return () => document.removeEventListener("focusin", surFocus);
  }, [open, engaged, modal, fermer]);

  // Mémorisée : une référence de rappel neuve à chaque rendu détacherait puis rattacherait le nœud (boucle de rendu).
  const composed = useMemo(() => composeRefs(ref, accessible, nodeRef), [ref, accessible]);
  if (!open && !forceMount) return null;
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      role="dialog"
      id={engine?.contentId}
      aria-labelledby={engine?.titleId}
      aria-describedby={engine?.described ? engine.descriptionId : undefined}
      aria-modal={modal ? true : undefined}
      data-state={open ? "open" : "closed"}
      data-motion={reduced ? "reduced" : undefined}
      tabIndex={-1}
      {...props}
      {...suspension(engaged, style)}
      ref={composed}
      onPointerDownCapture={(event) => {
        couche.marquerInterieur(event);
        onPointerDownCapture?.(event);
      }}
      onFocusCapture={(event) => {
        focusInterieurRef.current = event.nativeEvent;
        onFocusCapture?.(event);
      }}
    >
      {enfantSuspendu(asChild, engaged, children)}
    </Comp>
  );
});
